import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Header } from "@/components/Header";
import { api, euro } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Check, Sparkles, Loader2, Crown, X } from "lucide-react";

const BENEFITS = [
  "Alertes de prix avancées + notifications par email",
  "Historique de prix étendu et graphiques détaillés",
  "Menus IA illimités avec budget et équilibre nutritionnel",
  "Paniers famille & collaboratifs en temps réel",
  "Optimisation multi-magasins prioritaire",
];

const COMPARISON = [
  { label: "Comparaison multi-magasins", free: true, premium: true },
  { label: "Optimisation 2 magasins", free: true, premium: true },
  { label: "Menus IA de la semaine", free: "2 / jour", premium: "Illimités" },
  { label: "Historique de prix", free: "4 semaines", premium: "Complet" },
  { label: "Alertes email de baisse de prix", free: false, premium: true },
  { label: "Rappel avant fin d'essai", free: false, premium: true },
  { label: "Reçu par email", free: false, premium: true },
  { label: "Support prioritaire", free: false, premium: true },
];

const Cell = ({ v }) => {
  if (v === true) return <Check className="mx-auto h-4 w-4 text-primary" />;
  if (v === false) return <X className="mx-auto h-4 w-4 text-muted-foreground/40" />;
  return <span className="text-xs font-medium text-secondary">{v}</span>;
};

export default function Premium() {
  const { user, checked } = useAuth();
  const nav = useNavigate();
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState([]);
  const [selected, setSelected] = useState("premium_monthly");
  const [promo, setPromo] = useState("");
  const [promoInfo, setPromoInfo] = useState(null);
  const [checkingPromo, setCheckingPromo] = useState(false);

  useEffect(() => {
    api.get("/plans").then((r) => setPlans(r.data)).catch(() => {});
    const r = new URLSearchParams(window.location.search).get("ref");
    if (r) localStorage.setItem("pmc_ref", r);
  }, []);

  const monthly = plans.find((p) => p.lookup_key === "premium_monthly");
  const yearly = plans.find((p) => p.lookup_key === "premium_yearly");
  const yearlySavePct = monthly && yearly ? Math.round((1 - yearly.amount / (monthly.amount * 12)) * 100) : 0;
  const current = plans.find((p) => p.lookup_key === selected);

  const applyPromo = async () => {
    if (!promo.trim()) return;
    setCheckingPromo(true);
    try {
      const { data } = await api.get("/promo/validate", { params: { code: promo } });
      if (data.valid) { setPromoInfo(data); toast.success(`Code appliqué : -${data.percent_off}%`); }
      else { setPromoInfo(null); toast.error("Code promo invalide"); }
    } catch (e) { setPromoInfo(null); }
    finally { setCheckingPromo(false); }
  };

  const startCheckout = async () => {
    if (!checked) return;
    if (!user) { toast.message("Connectez-vous pour démarrer l'essai"); nav("/connexion"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: selected,
        origin_url: window.location.origin,
        user_id: user.id,
        ref: localStorage.getItem("pmc_ref") || undefined,
        promo_code: promoInfo?.valid ? promo.trim().toUpperCase() : undefined,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error("Impossible de démarrer le paiement");
      setLoading(false);
    }
  };

  return (
    <div className="App min-h-screen bg-background">
      <Header />
      <div className="mx-auto max-w-3xl px-4 py-16 sm:px-6">
        <div className="text-center">
          <Badge className="mb-4 rounded-full bg-accent/10 text-accent" data-testid="premium-badge">
            <Sparkles className="mr-1 h-3 w-3" /> Essai gratuit de 7 jours
          </Badge>
          <h1 className="font-display text-4xl font-800 tracking-tight text-secondary sm:text-5xl">
            Passez à <span className="text-primary">Premium</span>
          </h1>
          <p className="mt-4 text-muted-foreground">
            Débloquez toute la puissance de PanierMoinsCher pour économiser encore plus, chaque semaine.
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="tracing-beam mx-auto mt-10 max-w-md rounded-2xl bg-card p-8"
          data-testid="premium-card"
        >
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary text-white">
              <Crown className="h-6 w-6" />
            </div>
            <div>
              <p className="font-display text-lg font-800 text-secondary">PanierMoinsCher Premium</p>
              <p className="text-xs text-muted-foreground">Sans engagement · résiliable à tout moment</p>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-2 gap-3">
            {[monthly, yearly].filter(Boolean).map((p) => (
              <button
                key={p.lookup_key}
                onClick={() => setSelected(p.lookup_key)}
                className={`relative rounded-xl border-2 p-4 text-left transition-all ${selected === p.lookup_key ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"}`}
                data-testid={`plan-${p.lookup_key}`}
              >
                {p.lookup_key === "premium_yearly" && yearlySavePct > 0 && (
                  <Badge className="absolute -top-2 right-2 bg-accent text-white">-{yearlySavePct}%</Badge>
                )}
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{p.label}</p>
                <p className="mt-1 font-display text-2xl font-800 text-secondary">{euro(p.amount)}</p>
                <p className="text-xs text-muted-foreground">
                  / {p.period}{p.lookup_key === "premium_yearly" ? ` · ${euro(p.amount / 12)}/mois` : ""}
                </p>
              </button>
            ))}
          </div>
          <p className="mt-3 text-sm text-primary">
            7 jours gratuits, puis {current ? euro(current.amount) : "2,99 €"} / {current?.period || "mois"}
          </p>

          <ul className="mt-6 space-y-3">
            {BENEFITS.map((b, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-secondary" data-testid={`benefit-${i}`}>
                <Check className="mt-0.5 h-4 w-4 flex-none text-primary" /> {b}
              </li>
            ))}
          </ul>

          {user?.premium ? (
            <div className="mt-8 rounded-xl bg-primary/10 p-4 text-center text-sm font-semibold text-primary" data-testid="already-premium">
              Vous êtes déjà Premium 🎉
            </div>
          ) : (
            <>
              <div className="mt-6">
                <div className="flex gap-2">
                  <Input value={promo} onChange={(e) => setPromo(e.target.value)} placeholder="Code promo (ex: BIENVENUE)" className="rounded-full" data-testid="promo-input" />
                  <Button variant="outline" className="rounded-full" onClick={applyPromo} disabled={checkingPromo} data-testid="promo-apply-btn">
                    {checkingPromo ? <Loader2 className="h-4 w-4 animate-spin" /> : "Appliquer"}
                  </Button>
                </div>
                {promoInfo?.valid && (
                  <p className="mt-2 flex items-center gap-1 text-xs font-medium text-primary" data-testid="promo-applied">
                    <Check className="h-3 w-3" /> {promoInfo.name} appliqué (-{promoInfo.percent_off}%)
                  </p>
                )}
              </div>
              <Button onClick={startCheckout} disabled={loading || !checked} className="mt-4 w-full rounded-full text-base" size="lg" data-testid="start-trial-btn">
                {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
                Commencer l'essai gratuit
              </Button>
            </>
          )}
          <p className="mt-3 text-center text-xs text-muted-foreground">Paiement sécurisé par Stripe. Carte de test : 4242 4242 4242 4242</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mx-auto mt-14 max-w-2xl"
          data-testid="comparison-table"
        >
          <h2 className="text-center font-display text-2xl font-800 text-secondary">Gratuit vs Premium</h2>
          <div className="mt-6 overflow-hidden rounded-2xl border border-border bg-card">
            <div className="grid grid-cols-[1fr_auto_auto] items-center gap-4 border-b border-border bg-muted/50 px-5 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              <span>Fonctionnalité</span>
              <span className="w-20 text-center">Gratuit</span>
              <span className="w-20 text-center text-primary">Premium</span>
            </div>
            {COMPARISON.map((row, i) => (
              <div key={i} className="grid grid-cols-[1fr_auto_auto] items-center gap-4 border-b border-border px-5 py-3 last:border-0" data-testid={`compare-row-${i}`}>
                <span className="text-sm text-secondary">{row.label}</span>
                <span className="w-20 text-center"><Cell v={row.free} /></span>
                <span className="w-20 text-center"><Cell v={row.premium} /></span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
